import { notEmpty } from "@product/scmp-sdk";
import type { FastifyRequest } from "fastify";
import createMatcher from "feather-route-matcher";

import type { AppBarAdStatus, AppBarVariant } from "~/components/app-bar/helpers";
import { SubSectionList } from "~/components/story-style/consts";
import * as data from "~/data";
import { config } from "~/data";
import { Edition } from "~/lib/edition/data"; // This is for preventing server restart because of changing hooks
import { page as article, pathname as articlePathname } from "~/pages/article";
import { page as author, pathname as authorPathname } from "~/pages/author";
import { page as authors, pathname as authorsPathname } from "~/pages/authors";
import { page as home } from "~/pages/home";
import { page as menu, pathname as menuPathname } from "~/pages/menu";
import { page as newsletters, pathname as newslettersPathname } from "~/pages/newsletters";
import { page as page, pathname as pagePathname } from "~/pages/page";
import { page as podcast, pathname as podcastPathname } from "~/pages/podcast";
import { page as podcasts, pathname as podcastsPathname } from "~/pages/podcasts";
import { page as posties, pathname as postiesPathname } from "~/pages/posties";
import { page as postiesKids, pathname as postiesKidsPathname } from "~/pages/posties/kids";
import {
  page as postiesParents,
  pathname as postiesParentsPathname,
} from "~/pages/posties/parents";
import {
  page as postiesSection,
  pathname as postiesSectionPathname,
} from "~/pages/posties/section-index";
import { page as postiesVideo, pathname as postiesVideoPathname } from "~/pages/posties/video";
import { page as printArticle, pathname as printArticlePathname } from "~/pages/print-article";
import { page as search, pathname as searchPathname } from "~/pages/search";
import { page as section, pathname as sectionPathname } from "~/pages/section";
import { page as spotlight } from "~/pages/spotlight";
import {
  page as storyStyle,
  staticPathname as storyStyleStaticPathname,
} from "~/pages/story-style";
import { page as topic, pathname as topicPathname } from "~/pages/topic";
import { page as topics, pathname as topicsPathname } from "~/pages/topics";

import { ShouldForceV2RoutesCookieKey } from "./consts";

const pages = {
  article,
  author,
  authors,
  home,
  menu,
  newsletters,
  page,
  podcast,
  podcasts,
  posties,
  postiesKids,
  postiesParents,
  postiesSection,
  postiesVideo,
  printArticle,
  search,
  section,
  spotlight,
  storyStyle,
  topic,
  topics,
};

type StaticRouteFunction = (context: {
  params: Record<string, string>;
  query: Record<string, string>;
}) => {
  asPath?: string;
  cacheMaxAge?: number;
  pathname: string;
  query?: Record<string, string>;
};
const statics: Record<string, StaticRouteFunction> = {
  "/": () => pages.home.route({ edition: Edition.International, slide: "main" }),
  "/asia": () => pages.home.route({ edition: Edition.Asia, slide: "main" }),
  "/asia/opinion": () => pages.home.route({ edition: Edition.Asia, slide: "opinion" }),
  "/hk": () => pages.home.route({ edition: Edition.HongKong, slide: "main" }),
  "/hk/opinion": () => pages.home.route({ edition: Edition.HongKong, slide: "opinion" }),
  "/live": () => pages.home.route({ edition: Edition.HongKong, slide: "live" }),
  "/opinion": () => pages.home.route({ edition: Edition.International, slide: "opinion" }),
  // Treat drupal video sections as sections since content service does not include video section
  "/scmp-films": () =>
    pages.section.route({ entityId: data.section.scmpFilms.entityId, slide: "main" }),
  "/scmp-spotlight": () => pages.spotlight.route({ page: "spotlight" }),
  [`${searchPathname}/:value?`]: context =>
    pages.search.route({ value: context.query.value ?? context.params.value }),
  "/series": () => pages.spotlight.route({ page: "series" }),
  ...SubSectionList.reduce<Record<string, StaticRouteFunction>>((staticsRoutes, subSection) => {
    staticsRoutes[`${storyStyleStaticPathname}/${subSection}`] = () => ({
      ...pages.storyStyle.route({ subSection }),
      cacheMaxAge: 60,
    });
    return staticsRoutes;
  }, {}),
  "/talking-post": () => pages.section.route({ entityId: "511121", slide: "main" }),
  "/us": () => pages.home.route({ edition: Edition.UnitedStates, slide: "main" }),
  "/us/opinion": () => pages.home.route({ edition: Edition.UnitedStates, slide: "opinion" }),
  [authorsPathname]: context => pages.authors.route({ char: context.query.char }),
  [menuPathname]: () => pages.menu.route({}),
  [newslettersPathname]: () => pages.newsletters.route({}),
  [podcastsPathname]: () => pages.podcasts.route({}),
  [postiesKidsPathname]: () => ({ ...pages.postiesKids.route({}), cacheMaxAge: 60 }),
  [postiesParentsPathname]: () => ({ ...pages.postiesParents.route({}), cacheMaxAge: 60 }),
  [postiesPathname]: () => pages.posties.route({}),
  [postiesVideoPathname]: () => ({ ...pages.postiesVideo.route({}), cacheMaxAge: 60 }),
  [topicsPathname]: context => pages.topics.route({ char: context.query.char }),
};

export function isStaticRoute(url: string) {
  return Object.keys(routes.statics).some(staticRoute => staticRoute.startsWith(url));
}

type DynamicRouteFunctionContext = {
  appBarAdStatus?: AppBarAdStatus;
  appBarVariant?: AppBarVariant;
  entityUuid?: string;
  matchedPrefix: OrUndefined<string>;
  matchedSuffix: OrUndefined<string>;
  name?: string | null;
  params: Record<string, string>;
  parentSection?: string | null;
  query: Record<string, string>;
};

type DynamicRouteFunctionResult = {
  pathname: string;
  query?: Record<string, string>;
};

type DynamicRouteFunction = {
  (entityId: string, context?: DynamicRouteFunctionContext): DynamicRouteFunctionResult;
};

interface DynamicRoute {
  cacheMaxAge?: number;
  pathnames: string[];
  prefix?: string[];
  route: DynamicRouteFunction;
  suffix?: string[];
}

const dynamics: Record<string, DynamicRoute> = {
  Article: {
    pathnames: [articlePathname, printArticlePathname],
    prefix: [...pages.printArticle.prefix],
    route(entityId, context) {
      const matchedPrefix = context?.matchedPrefix;
      const commentID = context?.query.commentID;
      const entityUuid = context?.entityUuid ?? "";
      const appBarVariant = context?.appBarVariant;

      return matchedPrefix && pages.printArticle.prefix.includes(matchedPrefix)
        ? pages.printArticle.route({ entityId })
        : pages.article.route({
            appBarVariant,
            commentID,
            entityId,
            entityUuid,
          });
    },
  },
  Author: {
    cacheMaxAge: 60,
    pathnames: [authorPathname],
    route(entityId) {
      return pages.author.route({ entityId });
    },
  },
  Newsletter: {
    pathnames: [newslettersPathname, "/newsletter"],
    route(entityId) {
      return pages.newsletters.route({ entityId });
    },
  },
  Page: {
    pathnames: [pagePathname],
    route(entityId) {
      return pages.page.route({ entityId });
    },
  },
  Podcast: {
    pathnames: [podcastPathname],
    route(entityId) {
      return pages.podcast.route({ entityId });
    },
  },
  PodcastSection: {
    pathnames: [podcastPathname],
    route(entityId) {
      return pages.podcast.route({ entityId });
    },
  },
  Section: {
    cacheMaxAge: 60,
    pathnames: [sectionPathname, postiesSectionPathname],
    route(entityId, context) {
      const matchedSuffix = context?.matchedSuffix;
      const appBarVariant = context?.appBarVariant;
      const appBarAdStatus = context?.appBarAdStatus;
      const name = context?.name;
      const parentSection = context?.parentSection;
      if (parentSection === data.section.posties.read.entityUuid && name) {
        return pages.postiesSection.route({ entityId, name });
      }
      return pages.section.route({
        appBarAdStatus,
        appBarVariant,
        entityId,
        slide: pages.section.slideFromSuffix(matchedSuffix),
      });
    },
    suffix: [...pages.section.suffix],
  },
  Topic: {
    cacheMaxAge: 60,
    pathnames: [topicPathname],
    route(entityId) {
      return pages.topic.route({ entityId });
    },
  },
  Video: {
    cacheMaxAge: 60,
    pathnames: [postiesVideoPathname],
    route(entityId) {
      return pages.postiesVideo.route({ entityId });
    },
  },
};

// These redirects are scoped to the pwa application level for server side redirects.
// Client side should not need to handle these as there should not be any route define with old path.
const applicationRedirects = {
  "/frontpage/hk": "/hk",
  "/frontpage/international": "/",
  "/home": "/",
  "/newsletter": "/newsletters",
};

// These redirects are defined in the drupal.
// Client side should route these paths to server to resolve for the actual redirect value.
const drupalRedirects = [
  "/topics/coronavirus-outbreak",
  "/topics/coronavirus-pandemic",
  "/topics/explorers-tomorrow",
  "/comment/harrys-view",
];

export function isDrupalRedirect(url: string) {
  return drupalRedirects.some(redirect => url.startsWith(redirect));
}

// These patterns are handled by different applications with routing at ingress
const internals = [
  "/sport/racing",
  "/infographic",
  "/photos",
  "/video",
  "/cooking",
  "/yp",
  "/china-internet-report",
  "/knowledge",
];

// These patterns that belongs to external are specially handled by pwa internally
const excludedInternals = ["/knowledge/us-china-relations", "/knowledge/china-macro-economy"];

export function isInternalDomainRoute(url: string) {
  return (
    internals.some(internal => url.startsWith(internal)) &&
    !excludedInternals.some(excluded => url.startsWith(excluded))
  );
}

const rejects = ["/inkstone"];

// TODO: change all article related route to server side route after proxy is ready
const sponsorArticleRoute = {
  "/business-reports/*": true,
  "/country-reports/*": true,
  "/events/*": true,
  "/native/*": true,
  "/presented/*": true,
  "/special-reports/*": true,
  "/specialist-publications/*": true,
};

// Migrated is routes that v1 has migrated to v2
// Refs: https://www.npmjs.com/package/feather-route-matcher
const migratedRouteMatcher = createMatcher({
  ...sponsorArticleRoute,
  "/author(/*)": true,
  "/authors": true,
  "/newsletter(s)(/*)": true,
  "/posties(/*)": true,
  "/search(/*)": true,
  "/story/style(/*)": true,
  "/topic(/*)": true,
  "/topics(/*)": true,
});

// TODO: remove after starling widget is supported in v2
// Cspell:ignore legco
const starlingArticlesRoute = [
  "/news/hong-kong/politics/article/3176982/john-lee-vows-undertake-historic-mission-and-start-new",
  "/news/hong-kong/politics/article/3160321/hong-kong-legco-election-city-splits-parallel-universes",
  "/news/hong-kong/politics/article/3160333/hong-kong-legco-election-once-confident-pro-establishment",
  "/news/hong-kong/politics/article/3160475/what-hong-kong-election-results-reveal-about-new",
  "/news/hong-kong/politics/article/3160466/hong-kong-legislative-council-election-defeated-centrists",
  "/news/hong-kong/politics/article/3160469/hong-kong-elections-already-dominant-pro-establishment-camp",
];

function isV1Route(url: string) {
  return starlingArticlesRoute.some(route => url.startsWith(route));
}

export function isMigratedRoute(url: string, cookies: FastifyRequest["cookies"] = {}) {
  if (isV1Route(url)) return false;
  if (config.general.bypassMigrateRouteChecking) return true;
  const shouldForceV2Routes = cookies[ShouldForceV2RoutesCookieKey] === "true";
  return shouldForceV2Routes || notEmpty(migratedRouteMatcher(url));
}

export const routes = {
  applicationRedirects,
  drupalRedirects,
  dynamics,
  excludedInternals,
  internals,
  rejects,
  statics,
};
